import EventSlideNavigationButton from '@/components/feature/event/event-slide-navigation-button';
import EventGrid from '@/components/feature/event/event-grid';
import EventPageCount from '@/components/feature/event/event-page-count';
import type { EventSlide } from '@/types/event';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Navigation, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import EventGridHorizontal from '../event-grid-horizontal/EventGridHorizontal';
import { useConfigStore } from '@/stores/configStore';
import { useAccessibilityTTS, useScreenSize } from '@/hooks';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import 'swiper/css';
import 'swiper/css/navigation';

interface EventMainViewProps {
    eventSlidesHorizontal: EventSlide[][];
    eventSlidesVertical: EventSlide[][];
    onItemClick: (category: string) => void;
    isHorizontal: boolean;
}

export default function EventMainView({ eventSlidesHorizontal, eventSlidesVertical, onItemClick, isHorizontal }: EventMainViewProps) {
    // Refs for navigation buttons
    const prevButtonRef = useRef<HTMLButtonElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);
    const swiperRef = useRef<SwiperType | null>(null);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const isZoomed = useMemo(() => {
        return isZoomIn || (isZoomClosed === false && isZoomIn === false);
    }, [isZoomIn, isZoomClosed]);

    // TTS hooks
    const { speak } = useAccessibilityTTS();
    const voice = useConfigStore((state) => state.config.tts.voice);
    const eventQrImage = useConfigStore((state) => state.config.server?.building.eventQrImage);
    const { t } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();

    // Add debounce refs to prevent double execution
    const nextExecutingRef = useRef(false);
    const prevExecutingRef = useRef(false);

    const prevButtonText = t('floor_guide.text_to_speech.back');
    const nextButtonText = t('floor_guide.text_to_speech.next');

    const inactiveButtonText = t('common.button.tts.inactive_button');
    const activeButtonText = t('common.button.tts.button');

    // State for tracking navigation state
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    // Slide change handler
    const handleSlideChange = (swiper: SwiperType) => {
        setCurrentSlideIndex(swiper.activeIndex);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    // Navigation handlers with TTS support
    const handlePrevWithTTS = useCallback(() => {
        if (prevExecutingRef.current) {
            return;
        }

        prevExecutingRef.current = true;

        if (swiperRef.current && !isBeginning) {
            // Temporarily enable slide navigation for programmatic control
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slidePrev();

            // Restore original state after slide change
            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) {
                speak(t('event.slide.prev_btn_pressed', { page: currentSlideIndex }));
            }
        } else if (isBeginning) {
            if (voice) {
                speak(t('event.slide.prev_btn_disabled'));
            }
        }

        setTimeout(() => {
            prevExecutingRef.current = false;
        }, 100);
    }, [isBeginning, currentSlideIndex, voice, speak, t, isZoomed]);

    const handleNextWithTTS = useCallback(() => {
        if (nextExecutingRef.current) {
            return;
        }

        nextExecutingRef.current = true;

        if (swiperRef.current && !isEnd) {
            // Temporarily enable slide navigation for programmatic control
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slideNext();

            // Restore original state after slide change
            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) {
                speak(t('event.slide.next_btn_pressed', { page: currentSlideIndex + 2 }));
            }
        } else if (isEnd) {
            if (voice) {
                speak(t('event.slide.next_btn_disabled'));
            }
        }

        setTimeout(() => {
            nextExecutingRef.current = false;
        }, 100);
    }, [isEnd, currentSlideIndex, voice, speak, t, isZoomed]);

    // Get the appropriate slides based on orientation
    const currentSlides = isHorizontal ? eventSlidesHorizontal : eventSlidesVertical;
    // Reset state when slides change
    useEffect(() => {
        if (swiperRef.current) {
            setCurrentSlideIndex(0);
            setIsBeginning(true);
            setIsEnd(currentSlides.length <= 1);
            swiperRef.current.slideTo(0, 0);
        }
    }, [currentSlides.length]);

    useEffect(() => {
        if (swiperRef.current) {
            // Luôn giữ allowTouchMove = true để scroll content bên trong
            swiperRef.current.allowTouchMove = true;

            if (isZoomed) {
                // Prevent slide change nhưng vẫn cho phép scroll
                swiperRef.current.allowSlideNext = false;
                swiperRef.current.allowSlidePrev = false;
            } else {
                // Enable slide change khi không zoom
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }
        }
    }, [isZoomed]);

    return (
        <main
            className={`fullscreen:widescreen:px-[calc(171*var(--vw-unit))] widescreen:px-[calc(171*var(--vw-unit))] fullscreen:widescreen:pt-[calc(53*var(--vh-unit))] widescreen:pt-[calc(28*var(--vh-unit))] fullscreen:widescreen:pb-0 widescreen:pb-0 fullscreen:pt-[calc(24*var(--vh-unit))] fullscreen:px-[calc(208*var(--vw-unit))] flex h-full flex-col px-[calc(208*var(--vw-unit))] pt-[calc(49*var(--vh-unit))]`}
        >
            <div className="widescreen:fullscreen:pb-[calc(108*var(--vh-unit))] widescreen:pb-[calc(28*var(--vh-unit))] fullscreen:pb-[calc(21.5*var(--vh-unit))] relative pb-[calc(48*var(--vh-unit))]">
                {currentSlides.length > 1 && (
                    <div className="fullscreen:widescreen:mb-[calc(58*var(--vh-unit))] widescreen:mb-[calc(12*var(--vh-unit))] fullscreen:mb-[calc(38.5*var(--vh-unit))] mb-[calc(63*var(--vh-unit))] flex justify-center">
                        <EventPageCount contrast={contrast} page={currentSlideIndex + 1} totalPage={currentSlides.length} tabIndex={2} />
                    </div>
                )}

                <div className="relative">
                    {currentSlides.length > 0 && (
                        <>
                            <Swiper
                                modules={[Virtual, A11y, Navigation]}
                                allowSlideNext={!isZoomed}
                                allowSlidePrev={!isZoomed}
                                allowTouchMove={true}
                                virtual={{
                                    enabled: true,
                                    addSlidesBefore: 1,
                                    addSlidesAfter: 1,
                                }}
                                spaceBetween={0}
                                slidesPerView={1}
                                className="overflow-visible"
                                // Khi zoom: tăng threshold để khó swipe, nhưng vẫn cho scroll content
                                threshold={isZoomed ? 1000 : 5}
                                resistance={true}
                                resistanceRatio={isZoomed ? 1 : 0.85}
                                shortSwipes={!isZoomed}
                                longSwipes={!isZoomed}
                                followFinger={true}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                                onSlideChange={handleSlideChange}
                            >
                                {currentSlides.map((slideItems: EventSlide[], slideIndex: number) => {
                                    const isCurrentSlide = slideIndex === currentSlideIndex;
                                    const itemsWithTabIndex = slideItems.map((item) => ({
                                        ...item,
                                        tabIndex: isCurrentSlide ? 3 : -1,
                                    }));

                                    return (
                                        <SwiperSlide key={slideIndex}>
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    overflowY: 'hidden',
                                                    pointerEvents: 'auto',
                                                    touchAction: isZoomed ? 'pan-x pan-y' : 'auto',
                                                }}
                                                data-region-nav={isCurrentSlide}
                                                data-region-order={isCurrentSlide ? 3 : -1}
                                            >
                                                {isHorizontal ? (
                                                    <EventGridHorizontal slideItems={itemsWithTabIndex} onItemClick={onItemClick} />
                                                ) : (
                                                    <EventGrid slideItems={itemsWithTabIndex} onItemClick={onItemClick} />
                                                )}
                                            </div>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                            {currentSlides.length > 1 && (
                                <EventSlideNavigationButton
                                    data-region-nav
                                    data-region-order={4}
                                    data-tts-scope="carousel-navigation-button"
                                    navigationDirection="previous"
                                    ref={prevButtonRef as React.RefObject<HTMLButtonElement> | null}
                                    aria-label={t('event.slide.prev_btn')}
                                    isDisabled={isBeginning}
                                    tabIndex={4}
                                    role="button"
                                    data-role={t('common.data_role.button')}
                                    data-interactive
                                    className="swiper-button-prev-custom widescreen:left-[calc(-88*var(--vw-unit))] absolute top-1/2 left-[calc(-108*var(--vw-unit))] z-20 -translate-y-1/2"
                                    onFocus={() => {
                                        if (isBeginning) {
                                            speak(`${prevButtonText} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${prevButtonText} ${activeButtonText}`);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handlePrevWithTTS();
                                        }
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handlePrevWithTTS();
                                    }}
                                    onClick={() => {
                                        prevButtonRef.current?.focus();
                                    }}
                                />
                            )}

                            {currentSlides.length > 1 && (
                                <EventSlideNavigationButton
                                    data-region-nav
                                    data-region-order={5}
                                    data-tts-scope="carousel-navigation-button"
                                    navigationDirection="next"
                                    ref={nextButtonRef as React.RefObject<HTMLButtonElement> | null}
                                    aria-label={t('event.slide.next_btn')}
                                    isDisabled={isEnd}
                                    tabIndex={5}
                                    role="button"
                                    data-role={t('common.data_role.button')}
                                    data-interactive
                                    className="swiper-button-next-custom widescreen:right-[calc(-88*var(--vw-unit))] absolute top-1/2 right-[calc(-108*var(--vw-unit))] z-20 -translate-y-1/2"
                                    onFocus={() => {
                                        if (isEnd) {
                                            speak(`${nextButtonText} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${nextButtonText} ${activeButtonText}`);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleNextWithTTS();
                                        }
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleNextWithTTS();
                                    }}
                                    onClick={() => {
                                        nextButtonRef.current?.focus();
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            {/* TODO: Temporarily hide QR code */}
            {eventQrImage && (
                <div
                    tabIndex={6}
                    data-region-nav
                    data-region-order={6}
                    data-interactive
                    aria-label={t('event.QR')}
                    className="contrast:border-t-[calc(1*var(--vh-unit))] contrast:border-t-primary-white text-text-main01 widescreen:fullscreen:-mx-[calc(171*var(--vw-unit))] fullscreen:-mx-[calc(208*var(--vw-unit))] widescreen:-mx-[calc(171*var(--vw-unit))] focus-border -mx-[calc(208*var(--vw-unit))] flex h-full items-center justify-center gap-[calc(16*var(--vh-unit))] bg-[var(--color-eaeaea-to-black-222)] text-[calc(18*var(--vh-unit))] leading-[calc(24*var(--vh-unit))]"
                >
                    <QRCode value={eventQrImage} size={scaleHeightPixel(50)} />
                    {t('event.QR_info')}
                </div>
            )}
        </main>
    );
}
